import { pushToDataLayer, type DataLayerEvent } from '@/shared/lib/analytics/gtm'

export type InsightAuthoringTelemetrySink = (event: DataLayerEvent) => void

export interface InsightAuthoringTelemetry {
  trackSessionStarted: (input?: { sessionId?: string }) => void
  trackTurnSent: (input?: { sessionId?: string; messageLen?: number }) => void
  trackTurnReceived: (input?: {
    sessionId?: string
    turnId?: string | number
    warningsCount?: number
  }) => void
  trackDraftSaved: (input?: { sessionId?: string; configId?: number }) => void
  trackDryRunStarted: (input?: { configId?: number; workspaceId?: number }) => void
  trackDryRunCompleted: (input?: {
    configId?: number
    workspaceId?: number
    status?: string
    reason?: string
    elapsedMs?: number
  }) => void
  trackActivated: (input?: { configId?: number; targetStatus?: string }) => void
}

export function createInsightAuthoringTelemetry(
  sink: InsightAuthoringTelemetrySink = pushToDataLayer,
  now: () => Date = () => new Date(),
): InsightAuthoringTelemetry {
  function emit(event: string, payload: Record<string, unknown> = {}): void {
    sink({
      event: `insights_authoring_${event}`,
      event_type: 'Feature',
      feature_category: 'insights_admin_authoring',
      ...payload,
      timestamp: now().toISOString(),
    })
  }

  return {
    trackSessionStarted: (input = {}) =>
      emit('session_started', { session_id: input.sessionId || null }),
    trackTurnSent: (input = {}) =>
      emit('turn_sent', {
        session_id: input.sessionId || null,
        message_len: input.messageLen || 0,
      }),
    trackTurnReceived: (input = {}) =>
      emit('turn_received', {
        session_id: input.sessionId || null,
        turn_id: input.turnId ?? null,
        warnings_count: input.warningsCount ?? 0,
      }),
    trackDraftSaved: (input = {}) =>
      emit('draft_saved', {
        session_id: input.sessionId || null,
        config_id: input.configId ?? null,
      }),
    trackDryRunStarted: (input = {}) =>
      emit('dry_run_started', {
        config_id: input.configId ?? null,
        workspace_id: input.workspaceId ?? null,
      }),
    trackDryRunCompleted: (input = {}) =>
      emit('dry_run_completed', {
        config_id: input.configId ?? null,
        workspace_id: input.workspaceId ?? null,
        status: input.status || 'unknown',
        reason: input.reason || null,
        elapsed_ms: input.elapsedMs ?? null,
      }),
    trackActivated: (input = {}) =>
      emit('activated', {
        config_id: input.configId ?? null,
        target_status: input.targetStatus || 'active',
      }),
  }
}

const telemetry = createInsightAuthoringTelemetry()
export const trackSessionStarted = telemetry.trackSessionStarted
export const trackTurnSent = telemetry.trackTurnSent
export const trackTurnReceived = telemetry.trackTurnReceived
export const trackDraftSaved = telemetry.trackDraftSaved
export const trackDryRunStarted = telemetry.trackDryRunStarted
export const trackDryRunCompleted = telemetry.trackDryRunCompleted
export const trackActivated = telemetry.trackActivated
